import { Alert, Badge, Button, Card, EmptyState, List, ListRow, LoadingState, PageHeader, Stat, StatGrid, Tile } from '@platform/ui';
import { Link } from '@tanstack/react-router';

import { useLimitSentence } from '../components/PlanLimitAlert';
import { SetupChecklist } from '../components/SetupChecklist';
import { useMessages } from '../i18n';
import { useOwnStoreName } from '../lib/app-config';
import { trialDaysLeft, useSubscription } from '../lib/billing';
import { CHANNEL_STATUS_TONES, channelLabel, useChannelHealth, useConnectionNames } from '../lib/channels';
import { useDashboard } from '../lib/dashboard';
import { formatMoney, useFormat } from '../lib/format';
import { usePageTitle } from '../lib/page-title';
import { useCanOpen, useSession } from '../lib/session';

/**
 * The seller's overview.
 *
 * Two halves, deliberately. What happened — today's trade, with thirty days
 * behind it because one number alone says nothing about whether today is good.
 * And what is waiting, as separate tasks with the link that starts each, rather
 * than a single "issues" count nobody can act on.
 *
 * Nothing here is a vanity figure. A number that is neither something that
 * happened nor something to do belongs in a report, not on the first screen.
 */
export function DashboardPage() {
    const t = useMessages();
    usePageTitle(t.dashboard.title);
    const { me } = useSession();
    const canOpen = useCanOpen();
    const dashboard = useDashboard();
    // Named from the connection list the panel already holds, rather than
    // duplicated onto every dashboard row where the two could disagree.
    const channelNames = useConnectionNames();

    if (dashboard.isPending) {
        return <LoadingState label={t.common.loading} />;
    }
    if (dashboard.isError || !dashboard.data) {
        return <Alert tone="danger">{t.common.genericError}</Alert>;
    }

    const data = dashboard.data;
    // Narrowed once rather than at each use: TypeScript cannot see that the
    // guard below still holds inside the JSX expression.
    const todayRevenue = data.todayRevenue;
    const periodRevenue = data.periodRevenue;

    const tasks = [
        {
            key: 'actionRequired',
            count: data.actionRequired,
            label: t.dashboard.work.actionRequired,
            to: '/siparisler' as const,
            tone: 'warning' as const,
        },
        {
            key: 'unmatched',
            count: data.unmatchedLines,
            label: t.dashboard.work.unmatched,
            to: '/siparisler' as const,
            tone: 'danger' as const,
        },
        {
            key: 'pending',
            count: data.pendingListings,
            label: t.dashboard.work.pending,
            to: '/kanallar' as const,
            tone: 'accent' as const,
        },
        {
            key: 'failed',
            count: data.failedListings,
            label: t.dashboard.work.failed,
            to: '/urunler' as const,
            tone: 'danger' as const,
        },
        {
            key: 'openClaims',
            count: data.openClaims ?? 0,
            label: t.dashboard.work.openClaims,
            to: '/iadeler' as const,
            tone: 'warning' as const,
        },
        {
            key: 'failedInvoices',
            count: data.failedInvoices ?? 0,
            label: t.dashboard.work.failedInvoices,
            to: '/faturalar' as const,
            tone: 'danger' as const,
        },
        {
            key: 'failingChannels',
            count: data.failingChannels,
            label: t.dashboard.work.failingChannels,
            to: '/kanallar' as const,
            tone: 'danger' as const,
        },
    ].filter((task) => task.count > 0 && canOpen(task.to));

    // Each task is a link, because a count without a way to act on it is a
    // number rather than a task — which is also why a task whose page the
    // session may not open is left out above.
    const taskLink = 'flex min-h-11 items-center gap-3 py-2 hover:text-primary-strong';
    const taskText = 'flex min-h-11 items-center gap-3 py-2';

    return (
        <div className="flex flex-col gap-6">
            <PageHeader title={t.dashboard.title} description={t.dashboard.welcome(me?.user.name || '')} />

            <PlanNotice />

            <SetupChecklist />

            {/* No channel connected means nothing else on this screen can be true
                yet, so it replaces the figures rather than sitting under them. */}
            {data.connectedChannels === 0 ? (
                <Card>
                    <EmptyState
                        title={t.dashboard.emptyState}
                        description={t.dashboard.emptyStateHint}
                        action={
                            canOpen('/kanallar') && (
                                <Button asChild>
                                    <Link to="/kanallar">{t.nav.channels}</Link>
                                </Button>
                            )
                        }
                    />
                </Card>
            ) : (
                <>
                    <StatGrid>
                        <Stat label={t.dashboard.metrics.todayOrders} value={String(data.todayOrders)} />
                        {/* Revenue is absent for an employee without report.read — someone
                            packing parcels needs to know orders arrived, not what the
                            company turns over. The tile is hidden rather than showing zero,
                            which would read as a business doing no trade. */}
                        {todayRevenue !== undefined && <Stat label={t.dashboard.metrics.todayRevenue} value={formatMoney(todayRevenue, 'TRY')} />}
                        <Stat label={t.dashboard.metrics.periodOrders} value={String(data.periodOrders)} />
                        {periodRevenue !== undefined && <Stat label={t.dashboard.metrics.periodRevenue} value={formatMoney(periodRevenue, 'TRY')} />}
                        {/* Beside the revenue rather than tucked away: a period figure
                            with no returns next to it flatters itself, and returns are
                            what a seller watches when a listing goes wrong. */}
                        <Stat label={t.dashboard.metrics.returnedOrders} value={String(data.returnedOrders)} />
                        {data.returnedRevenue !== undefined && (
                            <Stat label={t.dashboard.metrics.returnedRevenue} value={formatMoney(data.returnedRevenue, 'TRY')} />
                        )}
                    </StatGrid>

                    {/* Where the trade actually came from. A single total cannot say,
                        and this is the question a seller asks before deciding where to
                        spend their attention. */}
                    {data.byChannel.length > 1 && (
                        <Card title={t.dashboard.byChannel.title}>
                            <List>
                                {data.byChannel.map((row) => (
                                    <ListRow
                                        key={row.channelAccountId}
                                        title={channelNames.get(row.channelAccountId) ?? t.dashboard.byChannel.unknown}
                                        aside={
                                            row.revenue !== undefined && <strong className="tabular-nums">{formatMoney(row.revenue, 'TRY')}</strong>
                                        }
                                    >
                                        {t.dashboard.byChannel.orders(row.orders)}
                                    </ListRow>
                                ))}
                            </List>
                        </Card>
                    )}

                    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(18rem,100%),1fr))] gap-4">
                        <Card title={t.dashboard.work.title}>
                            {tasks.length === 0 ? (
                                <EmptyState title={t.dashboard.work.none} description={t.dashboard.work.noneHint} />
                            ) : (
                                <ul className="flex flex-col divide-y divide-border">
                                    {tasks.map((task) => (
                                        <li key={task.key}>
                                            <Link to={task.to} className={taskLink}>
                                                <Badge tone={task.tone}>{task.count}</Badge>
                                                <span>{task.label}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Card>

                        <Card title={t.dashboard.lowStock.title}>
                            {data.lowStock.length === 0 ? (
                                <EmptyState title={t.dashboard.lowStock.none} />
                            ) : (
                                <ul className="flex flex-col divide-y divide-border">
                                    {data.lowStock.map((item) => {
                                        const to = item.productId ? '/urunler/$productId' : '/stok';
                                        const content = (
                                            <>
                                                <Badge tone={item.available === 0 ? 'danger' : 'warning'}>{item.available}</Badge>
                                                {/* The code goes under the name: a supplier's long code beside it
                                                    broke both into ragged lines. */}
                                                <span className="flex min-w-0 flex-col">
                                                    <span>{item.productTitle || item.sku}</span>
                                                    {item.productTitle && (
                                                        <code className="font-mono text-12 wrap-anywhere text-muted-foreground">{item.sku}</code>
                                                    )}
                                                </span>
                                            </>
                                        );
                                        return (
                                            <li key={item.variantId}>
                                                {/* Named rather than counted. "Twelve products are low"
                                                    is a statistic; three codes is somewhere to start. A
                                                    name whose page the session may not open stays text. */}
                                                {canOpen(to) ? (
                                                    <Link
                                                        to={to}
                                                        params={item.productId ? { productId: item.productId } : undefined}
                                                        className={taskLink}
                                                    >
                                                        {content}
                                                    </Link>
                                                ) : (
                                                    <div className={taskText}>{content}</div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </Card>
                    </div>

                    <ChannelSummary />
                </>
            )}
        </div>
    );
}

/**
 * The package, for as long as there is something to say about it: a trial with
 * its days left, a lapsed or unpaid subscription, a change bought for the end
 * of the period, or a limit the account is above (ADR 0017 §3) — with the way
 * to the packages (docs/15 §5). An active package with room needs no reminder
 * on the first screen, and someone who may not read billing is not asked
 * about it.
 */
function PlanNotice() {
    const t = useMessages();
    const format = useFormat();
    const canOpen = useCanOpen();
    const sentence = useLimitSentence();
    const subscription = useSubscription(canOpen('/abonelik'));
    const sub = subscription.data;
    const over = (sub?.limitStates ?? []).flatMap((state) => {
        const words = state.used > state.limit ? sentence(state) : undefined;
        return words ? [{ key: state.resource, words }] : [];
    });
    if (!sub || (sub.status === 'active' && !sub.scheduled && over.length === 0)) return null;

    const statusLabel = t.subscription.statuses[sub.status as keyof typeof t.subscription.statuses] ?? sub.status;
    const trialEnd = sub.status === 'trialing' ? sub.trialEndsAt : undefined;
    const daysLeft = trialEnd ? trialDaysLeft(trialEnd) : 0;

    return (
        <Card>
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                <div className="flex flex-col gap-1">
                    <span className="inline-flex items-center gap-2 font-semibold">
                        {sub.planName}
                        <Badge tone={sub.status === 'active' ? 'success' : sub.status === 'trialing' ? 'accent' : 'warning'}>{statusLabel}</Badge>
                    </span>
                    {trialEnd && (
                        <span className="text-13 text-muted-foreground">
                            {daysLeft > 0
                                ? t.dashboard.plan.trialLeft(daysLeft, format.date(trialEnd))
                                : t.dashboard.plan.trialOver(format.date(trialEnd))}
                        </span>
                    )}
                    {sub.scheduled && (
                        <span className="text-13 text-muted-foreground">
                            {t.subscription.scheduled(
                                format.date(sub.scheduled.at),
                                sub.scheduled.planName,
                                t.subscription.periods[sub.scheduled.period],
                            )}
                        </span>
                    )}
                    {over.map((notice) => (
                        <span key={notice.key} className="text-13 text-warning-strong">
                            {notice.words}
                        </span>
                    ))}
                </div>
                <Button asChild variant="secondary">
                    <Link to="/abonelik">{t.subscription.viewPlans}</Link>
                </Button>
            </div>
        </Card>
    );
}

/**
 * The connections at a glance, each a way into its own page, and the way to
 * the whole list. Counted as the channel list counts them (useChannelHealth),
 * so the two never disagree. Someone who cannot read the connections sees no
 * card rather than an empty one.
 */
function ChannelSummary() {
    const t = useMessages();
    const ownStoreName = useOwnStoreName();
    const health = useChannelHealth();
    if (health.rows.length === 0) return null;

    const active = health.rows.filter((row) => row.active).length;
    const broken = health.rows.filter((row) => row.broken).length;
    const note =
        broken > 0 ? t.dashboard.channelSummary.broken(broken) : active === health.rows.length ? t.dashboard.channelSummary.allWorking : undefined;

    return (
        <Card title={t.channels.title}>
            <div className="flex flex-wrap items-center gap-4">
                <div className="flex flex-[1_1_12rem] flex-col gap-1">
                    <span className="text-18 font-semibold tabular-nums">{t.dashboard.channelSummary.counts(health.rows.length, active)}</span>
                    {note && <span className="text-13 text-muted-foreground">{note}</span>}
                </div>
                {/* Equal widths, so the row reads as a set rather than a ragged line,
                    and one connection per line on a phone. */}
                <ul className="flex flex-[3_1_24rem] flex-wrap gap-3">
                    {health.rows.map(({ account, channel, status, active: working }) => {
                        // The own store has no connection to check, so it reads as open or closed.
                        const ownStore = channel?.kind === 'own_store';
                        const label = ownStore
                            ? working
                                ? t.channels.active
                                : t.channels.passive
                            : (t.channels.status[status as keyof typeof t.channels.status] ?? t.channels.status.pending);
                        const tone = ownStore ? (working ? 'success' : 'neutral') : (CHANNEL_STATUS_TONES[status] ?? 'neutral');
                        return (
                            <li key={account.id} className="flex-[1_1_14rem]">
                                <Link
                                    to="/kanallar/$accountId"
                                    params={{ accountId: account.id }}
                                    className="flex h-full min-h-11 items-center gap-3 rounded-card border border-border px-3 py-2 transition-colors hover:bg-accent"
                                >
                                    <Tile
                                        label={channelLabel(account, channel, ownStoreName)}
                                        image={channel?.logoUrl}
                                        imageDark={channel?.logoDarkUrl}
                                    />
                                    <span className="flex min-w-0 flex-col items-start gap-1">
                                        <span className="font-medium">{account.name}</span>
                                        <Badge tone={tone}>{label}</Badge>
                                    </span>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
                <Button asChild variant="secondary">
                    <Link to="/kanallar">{t.dashboard.channelSummary.manage}</Link>
                </Button>
            </div>
        </Card>
    );
}
